import { Divider, message, Modal } from 'antd'
import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import Head from '../../components/Head'
import List from '../../components/List'
import Button from '../../components/UI/Button'
import { deleteDevicesVersionById, exportCsvDevicesVersions, getDeviceVersionsList } from '../../services/deviceVersions'
import { getPermission } from '../../services/permisssion'
import permissionType from '../../utils/permissionType'
import {
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  HddOutlined
} from '@ant-design/icons';
import { changeDefaultServerById, changeDistributorStatus, changeServerStatus, deleteServerById, getDistributorServers, getDistributorsList } from '../../services/distributors'
import direction from '../../utils/filterDirectionOrder'
import { notificationError } from '../../services/notification'
import { useLoading } from '../../context/LoadingProvider/useLoading'
import { getNextServerStatusValue, getNextStatusValue, statusOptions } from '../../utils/status'
import { sendSystemLog, useAuth } from '../../context/AuthProvider/useAuth'

const distributorStatus = {
  active: 'ACTIVE',
  inactive: 'INACTIVE',
  closed: 'CLOSED',
}

function Distributors(props: { accessLevel: string }) {
  const history = useNavigate()
  const {canEdit} = useAuth()
  const [distributors, setDistributors] = useState([])
  const [filter, setFilter] = useState({
    fieldOrder: '',
    directionOrder: '',
  })
  const log = {
    log_type: 'visited',
    log_object: 'distributors'
  }
  const loading = useLoading()

  useEffect(() => {
    sendSystemLog(log);
  }, [])
  
  useEffect(() => {
    (async () => {
      loading.addLoading()
      const response = await getDistributorsList()
      if (response && response.list?.length > 0) {
        setDistributors(response.list)
      }
      loading.removeLoading()
    })()
  }, [])

  const onRefresh = async (order?: string, directionparam?: string) => {
    loading.addLoading()
    let requestParams = {
      fieldOrder: '',
      directionOrder: ''
    }
    if(order && directionparam) {
      requestParams = {
        ...requestParams,
        fieldOrder: order || '',
        directionOrder: directionparam === 'ascend' ? direction.asc : direction.desc,
      }
      setFilter({
        ...filter,
        fieldOrder: order || '',
        directionOrder: directionparam === 'ascend' ? direction.asc : direction.desc,
      })
    }
    const response = await getDistributorsList(requestParams)
    setDistributors(response.list)
    loading.removeLoading()
  }

  const changeStatusModal = (distributor: any) => {
    if(getNextStatusValue(distributor.status, 'status') === distributorStatus.active) {
      return onStatusChange(distributor)
    }

    Modal.confirm({
      title: 'Changing status',
      content: `The selected distributor, any child charity and related devices and cashiers will be ${getNextStatusValue(distributor.status, 'text')}. Are you sure?`,
      centered: true,
      onOk: () => onStatusChange(distributor),
      cancelText: 'Close',
    });
  }


  const onStatusChange = async (distributor: any) => {
    if (!distributor) return
    loading.addLoading()
    await changeDistributorStatus({
      id: distributor.id,
      status: getNextStatusValue(distributor.status, 'status'),
    })
    onRefresh()
    loading.removeLoading()
  }
  const onChangeOrder = (order: {column: any, field: string, order: string| undefined}) => {
    if(order.column) return onRefresh(order.field, order.order)
    return onRefresh()
  }

  const getActionButtons = (distributor: any) => {
    return (
      <div className='distributors__action-buttons'>
        <Button
          disabled={!canEdit}
          onClick={() => changeStatusModal(distributor)}
          type='primary'
          style={{
            backgroundColor: getNextStatusValue(distributor.status, 'color'),
            minWidth: '100px',
          }}
        >
          <p>{getNextStatusValue(distributor.status, 'label')}</p>
        </Button>
        <Button
          disabled={!canEdit}
          onClick={() => history(`/distributorservers/${distributor.name}/${distributor.id}`)}
          type='primary'
        >
          <HddOutlined style={{ fontSize: '16px', color: '#FFF' }} />
        </Button>
        <Button
          disabled={!canEdit}
          onClick={() => history(`/distributors/new/${distributor.id}`)}
          type='primary'
          style={{ backgroundColor: '#2fa4e7' }}
        >
          <EditOutlined style={{ fontSize: '16px', color: '#FFF' }} />
        </Button>
      </div>
    )
  }

  const getStatusLabel = (status: string) => {
    if (status === distributorStatus.active) {
      return <div className="distributors__table__status distributors__table__status--active">{status}</div>
    }
    if (status === distributorStatus.inactive) {
      return <div className="distributors__table__status distributors__table__status--inactive">{status}</div>
    }
    return <div className="distributors__table__status distributors__table__status--closed">{status}</div>
  }

  return (
    <div className="distributors">
      <meta name="viewport" content="width=device-width, initial-scale=.75"/>
      <Head title="All Distributors" buttonText='New Distributor' buttonAction={() => history('/distributors/new')} disabled={!canEdit}/>
      <List
        rows={distributors}
        onChange={(selectedRowKeys, selectedRows, order) => onChangeOrder(order)}
        columns={[
          {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
            render: (value: string) => value,
            sorter: true,
            width: 150
          },
          {
            title: 'Address',
            dataIndex: 'address',
            key: 'address',
            render: (value: string) => value,
            sorter: true,
            width: 150
          },
          {
            title: 'Zipcode',
            dataIndex: 'zipcode',
            key: 'zipcode',
            render: (value: string) => value,
            sorter: true,
            width: 150
          },
          {
            title: 'City',
            dataIndex: 'city',
            key: 'city',
            render: (value: string) => value,
            sorter: true,
            width: 150
          },
          {
            title: 'State',
            dataIndex: 'state',
            key: 'state',
            render: (value: string) => value,
            sorter: true,
            width: 150
          },
          {
            title: 'Phone',
            dataIndex: 'phone',
            key: 'phone',
            render: (value: string) => value,
            sorter: true,
            width: 100
          },
          {
            title: 'Email',
            dataIndex: 'email',
            key: 'email',
            render: (value: string) => value,
            sorter: true,
            width: 200,
          },
          {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (value: string) => getStatusLabel(value),
            sorter: true,
            width: 130
          },
          {
            title: 'Pin Code',
            dataIndex: 'pinCode',
            key: 'pinCode',
            render: (value: string) => value,
            sorter: true,
            width: 130
          },
          {
            title: 'Actions',
            dataIndex: '',
            key: '',
            render: (value: any) => getActionButtons(value),
            width: 230
          },
        ]}
      />
    </div>
  )
}

export default Distributors